import React, { useEffect, useRef, useState } from 'react';
import { Check, UserCheck, X } from 'lucide-react';
import { vipAPI } from '../../../services/api';
import { Textarea } from '../../ui/textarea';
import { Button } from '../../ui/button';
import { cn } from '../../../lib/utils';
import ContactFormFields, { emptyContactForm, useContactChannels } from './ContactFormFields';
import {
  apiError, BATCH_STATUS, batchBody, batchFollowUpHint, batchHint, batchRows, batchSummary, batchUserIds,
  contactFormErrors, formatWeekday, parseBatchEntries, playersLabel,
} from './trackerView';

const MAX_BATCH = 500;
const TOAST_MS = 2800;

function Panel({ step, title, id, className, children }) {
  return (
    <section aria-labelledby={id} className={cn('flex min-w-0 flex-[1_1_520px] flex-col rounded-2xl border border-white/10 bg-surface-1', className)}>
      {children(
        <div className="flex items-center gap-2.5">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-surface-2 text-xs font-bold text-ink-dim">{step}</span>
          <h3 id={id} className="text-base font-bold text-ink">{title}</h3>
        </div>,
      )}
    </section>
  );
}

function ResultList({ rows, onRemove, onRemoveBad }) {
  const matched = rows.filter((r) => r.status === 'ok').length;
  const bad = rows.length - matched;
  return (
    <div className="flex flex-col overflow-hidden rounded-xl border border-white/10">
      <div className="flex flex-wrap items-center gap-2 border-b border-white/10 bg-surface px-3.5 py-3">
        <span className="rounded-full bg-positive/15 px-2 py-0.5 text-xs font-bold text-positive">{matched} matched</span>
        {bad > 0 && <span className="rounded-full bg-negative/15 px-2 py-0.5 text-xs font-bold text-negative">{bad} won't be logged</span>}
        <span className="flex-1" />
        {bad > 0 && (
          <Button type="button" variant="outline" size="sm" className="h-8 text-xs font-medium" onClick={onRemoveBad}>
            Remove unmatched
          </Button>
        )}
      </div>
      <ul className="max-h-[260px] overflow-y-auto">
        {rows.map((r) => {
          const ok = r.status === 'ok';
          return (
            <li key={r.value} className="flex items-center gap-3 border-b border-white/5 py-2 pl-3.5 pr-2">
              <span className={cn('h-2 w-2 shrink-0 rounded-full', ok ? 'bg-positive' : 'bg-negative')} />
              {r.player ? (
                // Matched: always both identifiers, whichever was pasted.
                <span
                  className="flex min-w-0 flex-1 items-baseline gap-3 font-mono text-[13px]"
                  title={`${r.player.nickname || r.player.email || r.player.user_id} · ${r.player.vip_agent || 'Unassigned'}`}
                >
                  <span className="min-w-0 flex-1 truncate text-ink">{r.player.email || '—'}</span>
                  <span className="shrink-0 text-ink-dim">{r.player.user_id}</span>
                </span>
              ) : (
                <span className="min-w-0 flex-1 truncate font-mono text-[13px] text-ink">{r.value}</span>
              )}
              <span className={cn('whitespace-nowrap text-xs', ok ? 'text-ink-dim' : 'text-negative')}>{BATCH_STATUS[r.status]}</span>
              <Button type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-ink-mute" aria-label={`Remove ${r.value}`} onClick={() => onRemove(r.value)}>
                <X className="h-3.5 w-3.5" />
              </Button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// BatchLogTab logs one outreach sent to many players (e.g. the same email): paste emails or
// user IDs, check who they match, then save one log row per matched player.
export default function BatchLogTab() {
  const [text, setText] = useState('');
  const [resolved, setResolved] = useState(null);
  const [excluded, setExcluded] = useState([]);
  const [checking, setChecking] = useState(false);
  const [form, setForm] = useState(emptyContactForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const toastTimer = useRef(null);
  // checkSeq: bumped on every edit, so a check answering for an earlier list is dropped.
  const checkSeq = useRef(0);
  const preselect = (channelId) => setForm((f) => ({ ...f, channelId }));
  const { channels, error: channelsError } = useContactChannels(true, preselect);
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  const parsed = parseBatchEntries(text);
  const { entries } = parsed;
  const checked = Boolean(resolved);
  const rows = batchRows(entries, resolved, excluded);
  const userIds = batchUserIds(rows);
  const n = checked ? userIds.length : 0;
  const ready = checked && n > 0 && contactFormErrors(form).length === 0;

  const editText = (value) => {
    checkSeq.current += 1;
    setText(value);
    setResolved(null);
    setExcluded([]);
  };

  const check = async () => {
    if (entries.length === 0 || checking) return;
    const seq = checkSeq.current;
    setChecking(true);
    setError('');
    try {
      const res = await vipAPI.resolveContactPlayers(entries);
      if (seq !== checkSeq.current) return;
      setResolved(res.data);
      setExcluded([]);
    } catch (err) {
      if (seq === checkSeq.current) setError(apiError(err, 'Failed to check the players.'));
    } finally {
      setChecking(false);
    }
  };

  const save = async () => {
    if (!ready || saving) return;
    setSaving(true);
    setError('');
    try {
      const res = await vipAPI.logContactBatch(batchBody(form, userIds));
      const next = form.nextContactAt;
      setToast(`Logged contact for ${playersLabel(res.data.logged)}${next ? ` · next due ${formatWeekday(next)}` : ''}`);
      clearTimeout(toastTimer.current);
      toastTimer.current = setTimeout(() => setToast(''), TOAST_MS);
      editText('');
      setForm((f) => ({ ...f, note: '' }));
    } catch (err) {
      setError(apiError(err, 'Failed to save the batch.'));
    } finally {
      setSaving(false);
    }
  };

  const shownError = error || channelsError;
  const canCheck = entries.length > 0;
  return (
    <div className="relative flex flex-wrap items-stretch gap-6">
      <Panel step={1} title="Players" id="b-players" className="gap-4 p-6">
        {(heading) => (
          <>
            {heading}
            <label htmlFor="b-paste" className="text-[13px] leading-relaxed text-ink-dim">
              Paste the emails or user IDs the outreach went to. One per line, or separated by commas or spaces. Up to {MAX_BATCH}.
            </label>
            <Textarea
              id="b-paste" value={text} onChange={(e) => editText(e.target.value)}
              placeholder={'ana@example.com\nbob@example.com\n6887065'}
              className="min-h-[160px] resize-y font-mono leading-relaxed"
            />
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex-1 text-[13px] text-ink-dim">{batchSummary(parsed)}</div>
              <Button
                type="button" variant="outline" onClick={check} disabled={!canCheck || checking}
                className={cn('h-11 gap-2', canCheck && !checked && 'border-cta bg-cta/10')}
              >
                <UserCheck className="h-4 w-4" />
                {checking ? 'Checking…' : checked ? 'Check again' : 'Check players'}
              </Button>
            </div>
            {checked && rows.length > 0 && (
              <ResultList
                rows={rows}
                onRemove={(v) => setExcluded((x) => [...x, v])}
                onRemoveBad={() => setExcluded((x) => [...x, ...rows.filter((r) => r.status !== 'ok').map((r) => r.value)])}
              />
            )}
          </>
        )}
      </Panel>

      <Panel step={2} title="Contact" id="b-contact">
        {(heading) => (
          <form className="flex flex-1 flex-col" onSubmit={(e) => { e.preventDefault(); save(); }}>
            <div className="flex flex-1 flex-col gap-6 p-6">
              {heading}
              <ContactFormFields
                form={form} onChange={setForm} channels={channels} onSubmitShortcut={save}
                noteNote="Required · same note for every player"
                notePlaceholder="What was sent and offered, e.g. the campaign name and bonus."
                noteClassName="min-h-[140px]"
                followText={batchFollowUpHint(form.nextContactAt, n)}
              />
            </div>
            <div className="flex flex-wrap items-center gap-3 border-t border-white/10 px-6 py-4">
              <div className={cn('flex-[1_1_200px] text-xs', shownError ? 'text-negative' : 'text-ink-mute')} role={shownError ? 'alert' : undefined}>
                {shownError || batchHint({ entries, checked, rows, form })}
              </div>
              <Button type="submit" className="h-11 whitespace-nowrap" disabled={!ready || saving}>
                {saving ? 'Saving…' : `Log contact for ${playersLabel(n)}`}
              </Button>
            </div>
          </form>
        )}
      </Panel>

      {toast && (
        <div role="status" className="fixed bottom-7 left-1/2 z-40 flex -translate-x-1/2 items-center gap-2.5 whitespace-nowrap rounded-lg border border-cta bg-surface-2 px-4 py-3 text-[13px] font-medium text-ink shadow-xl">
          <Check className="h-4 w-4 text-cta" />
          {toast}
        </div>
      )}
    </div>
  );
}
